"use client";

import { type ReactNode, useEffect, useRef } from "react";
import Pusher from "pusher-js";
import { useDebouncedCallback } from "use-debounce";
import { configureEcho, useEchoPublic } from "@laravel/echo-react";
import { useParams } from "react-router-dom";

import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";
import { useChatStore } from "@/features/chat/store/useChatStore.tsx";
import envs from "@/envs.ts";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import { getConversationData } from "@/features/chat/api/chatApi.tsx";
import { NotificationSoundManager } from "@/features/classroom/utils/notificationSound";

interface Props {
  children: ReactNode;
}

export default function EventsWrapper({ children }: Props) {
  const { user } = useUserStore();
  const { lessonId } = useParams();
  const soundManager = useRef<NotificationSoundManager | null>(null);

  // Initialize sound manager
  useEffect(() => {
    soundManager.current = NotificationSoundManager.getInstance();
  }, []);

  // Function to fetch conversation details and add to open conversations
  const fetchAndOpenConversation = async (conversationId: string, fromFeedUpdate = false) => {
    try {
      // Get the current state directly from the store instead of using the closed-over variable
      const currentOpenConversations = useChatStore.getState().openConversations;
      const isAlreadyOpen = currentOpenConversations.find((conv) => conv.id === conversationId);

      if (!isAlreadyOpen) {
        const {
          data: { data: conversationData },
        } = await getConversationData(conversationId);

        // Use the store's setter to ensure atomic updates
        useChatStore.setState((state) => ({
          openConversations: [...state.openConversations, conversationData],
        }));

        // Play notification sound when opening a conversation from feed update (new message)
        if (fromFeedUpdate && soundManager.current) {
          soundManager.current.playNotification();
        }
      } else if (fromFeedUpdate && soundManager.current) {
        soundManager.current.playNotification();
      }
    } catch (error) {
      console.error("Failed to fetch conversation details:", error);
    }
  };

  const debouncedFetchAndOpenConversation = useDebouncedCallback(
    (id: string, fromFeedUpdate = false) => fetchAndOpenConversation(id, fromFeedUpdate),
    300,
  );

  Pusher.logToConsole = true;
  configureEcho({
    broadcaster: "pusher",
    key: envs.VITE_PUSHER_KEY,
    cluster: envs.VITE_PUSHER_CLUSTER,
    encrypted: true,
    authorizer: (channel: any) => {
      return {
        authorize: async (socketId: string, callback: Function) => {
          try {
            const response = await axiosApiInstance.post(`${API_ENDPOINTS.PUSHER_AUTH}`, {
              socket_id: socketId,
              channel_name: channel.name,
            });

            callback(false, response.data);
          } catch (error) {
            console.error("Pusher authentication error:", error);
            callback(true, null);
          }
        },
      };
    },
  });

  // Existing conversation feed update listener
  const { listen, stopListening } = useEchoPublic(
    `private-conversations.${user?.id}`,
    ".feed_update",
    (data: any) => {
      if (data.special_tag && data.special_tag === `lesson_${lessonId}`) return;
      debouncedFetchAndOpenConversation(data.conversation_id, true);
    },
    [user, lessonId],
  );

  useEffect(() => {
    if (!location.pathname.includes("chat")) {
      listen();
    } else {
      stopListening();
    }
  }, [location.pathname]);

  return <>{children}</>;
}
